import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Settings } from 'lucide-react';
import { checkAdminExists as checkAdmin, getCurrentUser, signOut, User } from '../lib/auth';

export default function Navbar() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showAdminSetup, setShowAdminSetup] = useState(false);
  const location = useLocation();

  useEffect(() => {
    checkAdminExists();
    loadUser();
  }, [location.pathname]);

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  };

  const checkAdminExists = async () => {
    try {
      const exists = await checkAdmin();
      setShowAdminSetup(!exists);
    } catch (err) {
      console.error('Failed to check admin status:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setUser(null);
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className="relative">
                <img
                  src="/Agos_logo.png"
                  alt="Agos Logo"
                  className="h-8 w-8 object-contain group-hover:scale-110 transition-transform"
                />
                <div className="absolute inset-0 bg-blue-500/20 blur-lg group-hover:bg-blue-400/30 transition-all" />
              </div>
              <span className="ml-2 text-xl font-bold text-white flex items-center gap-2">
                Agos Identity
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border border-blue-500/50 bg-blue-500/10 text-blue-400">
                  BETA
                </span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/pricing"
              className={`text-slate-300 hover:text-white transition-colors ${
                location.pathname === '/pricing' ? 'text-white font-semibold' : ''
              }`}
            >
              Pricing
            </Link>
            <Link
              to="/verify"
              className={`text-slate-300 hover:text-white transition-colors ${
                location.pathname === '/verify' ? 'text-white font-semibold' : ''
              }`}
            >
              Verify
            </Link>
            <Link
              to="/faq"
              className={`text-slate-300 hover:text-white transition-colors ${
                location.pathname === '/faq' ? 'text-white font-semibold' : ''
              }`}
            >
              FAQ
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`text-slate-300 hover:text-white transition-colors ${
                    location.pathname === '/dashboard' ? 'text-white font-semibold' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-slate-300 hover:text-white transition-colors"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-slate-300 hover:text-white transition-colors ${
                    location.pathname === '/login' ? 'text-white font-semibold' : ''
                  }`}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-5 py-2 rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-slate-300 hover:text-white focus:outline-none transition-colors"
              aria-expanded={isOpen}
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50">
            <div className="pt-2 pb-3 space-y-1">
              <Link
                to="/pricing"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/verify"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Verify
              </Link>
              <Link
                to="/faq"
                className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                onClick={() => setIsOpen(false)}
              >
                FAQ
              </Link>
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="block px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block mx-3 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white text-center rounded-lg hover:from-blue-500 hover:to-blue-400 transition-all"
                    onClick={() => setIsOpen(false)}
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}